<template>
  <div class="space-y-6">
    <!-- 헤더와 파일 업로드 -->
    <div class="card-base p-6">
      <div class="page-header flex justify-between items-start mb-6">
        <div>
          <h2 class="page-title">통안채 기본정보</h2>
          <p class="page-subtitle">
            통안채 데이터를 관리하고 매일 업데이트합니다
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <BaseButton variant="primary" icon="fas fa-plus" @click="openCreateModal">
            통안채 추가
          </BaseButton>
        </div>
      </div>

      <!-- 파일 업로드 영역 -->
      <div class="bg-gray-50 rounded-lg p-4 mb-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-sm font-medium text-gray-900">일자별 가격정보</h3>
            <p class="text-xs text-gray-500 mt-1">
              통안_YYYYMMDD.xlsx 형식의 파일을 업로드하세요
            </p>
          </div>
          <div class="text-xs text-gray-500">기준일: {{ selectedDate }}</div>
        </div>

        <div class="flex items-center space-x-3">
          <label class="btn-secondary cursor-pointer">
            <i class="fas fa-file-upload mr-2"></i>
            파일 선택
            <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleFileSelect" />
          </label>

          <BaseButton variant="secondary" icon="fas fa-upload" :disabled="!selectedFile || isUploading"
            :loading="isUploading" @click="uploadFile">
            업로드
          </BaseButton>

          <BaseButton variant="outline" icon="fas fa-chart-line" :disabled="isAnalyzing" :loading="isAnalyzing"
            @click="analyzePriceData">
            가격정보 분석
          </BaseButton>

          <!-- 구분선 -->
          <div class="h-8 w-px bg-gray-300"></div>

          <BaseButton variant="primary" icon="fas fa-external-link-alt" :disabled="isRefreshingPrices"
            :loading="isRefreshingPrices" @click="refreshInfomaxPrices">
            INFOMAX 민평 일괄 업데이트
          </BaseButton>
        </div>

        <!-- 선택된 파일명 표시 -->
        <div v-if="selectedFileName"
          class="flex items-center space-x-2 mt-3 text-gray-600 bg-white px-3 py-2 rounded border text-sm">
          <i class="fas fa-file-excel text-green-600"></i>
          <span class="truncate">{{ selectedFileName }}</span>
          <button @click="clearFile" class="text-gray-400 hover:text-gray-600 ml-auto">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 통안채 거래내역 업로드 -->
    <div class="card-base p-6">
      <div class="page-header mb-6">
        <div>
          <h2 class="page-title">통안채 거래내역</h2>
          <p class="page-subtitle">
            통안채 거래내역 데이터를 관리하고 매일 업데이트합니다
          </p>
        </div>
      </div>

      <!-- 거래내역 파일 업로드 영역 -->
      <div class="bg-gray-50 rounded-lg p-4 mb-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-sm font-medium text-gray-900">
              통안채 거래내역 업로드
            </h3>
            <p class="text-xs text-gray-500 mt-1">
              통안거래내역_YYYYMMDD.xlsx 형식의 파일을 업로드하세요
            </p>
          </div>
          <div class="text-xs text-gray-500">기준일: {{ selectedDate }}</div>
        </div>

        <div class="flex items-center space-x-3">
          <label class="btn-secondary cursor-pointer">
            <i class="fas fa-file-upload mr-2"></i>
            파일 선택
            <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleTransactionFileSelect" />
          </label>

          <BaseButton variant="secondary" icon="fas fa-upload"
            :disabled="!selectedTransactionFile || isUploadingTransaction" :loading="isUploadingTransaction"
            @click="uploadTransactionFile">
            업로드
          </BaseButton>

          <BaseButton variant="outline" icon="fas fa-chart-line" :disabled="isAnalyzingTransaction"
            :loading="isAnalyzingTransaction" @click="analyzeTransactionData">
            거래내역 분석
          </BaseButton>

          <!-- 구분선 -->
          <div class="h-8 w-px bg-gray-300"></div>

          <BaseButton variant="primary" icon="fas fa-external-link-alt" :disabled="isRefreshingTransactionPrices"
            :loading="isRefreshingTransactionPrices" @click="refreshInfomaxTransactions">
            INFOMAX 거래내역 업데이트
          </BaseButton>
        </div>

        <!-- 선택된 파일명 표시 -->
        <div v-if="selectedTransactionFileName"
          class="flex items-center space-x-2 mt-3 text-gray-600 bg-white px-3 py-2 rounded border text-sm">
          <i class="fas fa-file-excel text-green-600"></i>
          <span class="truncate">{{ selectedTransactionFileName }}</span>
          <button @click="clearTransactionFile" class="text-gray-400 hover:text-gray-600 ml-auto">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 통안채 관리 그리드 -->
    <div class="card-base p-6">
      <div class="page-header mb-6">
        <h3 class="text-lg font-semibold text-gray-900">통안채 관리</h3>
        <p class="text-sm text-gray-600 mt-1">
          드래그하여 통안채 유형을 변경할 수 있습니다
        </p>
      </div>

      <div class="grid grid-cols-8 gap-6">
        <MsbBondColumn v-for="type in typeOrder" :key="type" :type="type" :type-name="getMsbTypeName(type)"
          :bonds="getFilteredBonds(type)" :is-drag-over="isDragOverTarget(type)" :dragged-bond="draggedBond"
          @drop="handleDrop" @dragover="onDragOver" @dragleave="onDragLeave" @dragstart="onDragStart"
          @dragend="onDragEnd" @edit="openEditModal" />
      </div>
    </div>

    <!-- 생성 모달 -->
    <BaseModal v-model="showCreateModal" title="통안채 추가" size="lg" @close="closeCreateModal">
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <BaseInput v-model="createForm.bondName" label="채권명" placeholder="통안채명을 입력하세요" required />

          <BaseInput v-model="createForm.code" label="채권코드" placeholder="채권코드를 입력하세요" class="font-mono" required />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">통안채 유형</label>
            <select v-model="createForm.msbType" class="select-base">
              <option value="">유형을 선택하세요</option>
              <option v-for="(name, type) in msbTypes" :key="type" :value="type">
                {{ name }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">금리 (%)</label>
            <div class="relative">
              <input type="number" v-model="createForm.yield" step="0.001" class="input-base pr-10"
                placeholder="2.500" />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span class="text-gray-500">%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <BaseInput v-model="createForm.maturityDate" type="date" label="만기일" required />

          <BaseInput v-model="createForm.issueDate" type="date" label="발행일" required />
        </div>

        <BaseInput v-model="createForm.price" type="number" label="가격" placeholder="100.00" step="0.01" />
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="closeCreateModal">
          취소
        </BaseButton>
        <BaseButton variant="primary" @click="handleCreate" :loading="isLoading">
          추가
        </BaseButton>
      </template>
    </BaseModal>

    <!-- 수정 모달 -->
    <BaseModal v-model="showEditModal" title="통안채 수정" size="lg" @close="closeEditModal">
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">채권명</label>
            <div class="input-base bg-gray-50 text-gray-600">
              {{ selectedBond?.bondName }}
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">채권코드</label>
            <div class="input-base bg-gray-50 text-gray-600 font-mono">
              {{ selectedBond?.code }}
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">통안채 유형</label>
            <select v-model="editForm.msbType" class="select-base">
              <option v-for="(name, type) in msbTypes" :key="type" :value="type">
                {{ name }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">금리 (%)</label>
            <div class="relative">
              <input type="number" v-model="editForm.yield" step="0.001" class="input-base pr-10" />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span class="text-gray-500">%</span>
              </div>
            </div>
          </div>
        </div>

        <BaseInput v-model="editForm.price" type="number" label="가격" step="0.01" />
      </div>

      <template #footer>
        <BaseButton variant="outline" @click="closeEditModal">
          취소
        </BaseButton>
        <BaseButton variant="primary" @click="handleUpdate" :loading="isLoading">
          저장
        </BaseButton>
      </template>
    </BaseModal>

    <!-- 로딩 오버레이 -->
    <div
      v-if="isLoading || isRefreshingPrices || isUploadingTransaction || isAnalyzingTransaction || isRefreshingTransactionPrices"
      class="loading-overlay">
      <div class="bg-white rounded-xl p-8 shadow-2xl">
        <div class="flex items-center space-x-4">
          <div class="loading-spinner"></div>
          <div class="text-center">
            <span class="text-gray-700 font-medium block">
              {{ getLoadingMessage() }}
            </span>
            <span v-if="isRefreshingPrices" class="text-gray-500 text-sm mt-2 block">
              잠시만 기다려 주세요. 다른 작업을 하지 마세요.
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseModal from '@/components/common/BaseModal.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import MsbBondColumn from './msb/MsbBondColumn.vue';
import { useMsbBonds } from '@/composables/useMsbBonds';
import { useDragAndDrop } from '@/composables/useDragAndDrop';
import bondService from '@/service/bondService';
import transactionService from '@/service/transactionService';

export default {
  name: 'MsbBasicComponent',
  components: {
    BaseButton,
    BaseModal,
    BaseInput,
    MsbBondColumn,
  },
  props: {
    selectedDate: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    // Composables
    const {
      isLoading,
      msbTypes,
      typeOrder,
      loadMsbBonds,
      createMsbBond,
      updateMsbBond,
      updateMsbType,
      getMsbTypeName,
      getFilteredBonds,
    } = useMsbBonds();

    const {
      draggedItem: draggedBond,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDragLeave,
      onDrop,
      isDragOverTarget,
    } = useDragAndDrop();

    // File upload states
    const selectedFile = ref(null);
    const selectedFileName = ref('');
    const isUploading = ref(false);
    const isAnalyzing = ref(false);
    const isRefreshingPrices = ref(false);
    // Props에서 받은 선택된 날짜 사용

    // Transaction file upload states
    const selectedTransactionFile = ref(null);
    const selectedTransactionFileName = ref('');
    const isUploadingTransaction = ref(false);
    const isAnalyzingTransaction = ref(false);
    const isRefreshingTransactionPrices = ref(false);

    // Modal states
    const showCreateModal = ref(false);
    const showEditModal = ref(false);
    const selectedBond = ref(null);

    // Form states
    const createForm = ref({
      bondName: '',
      code: '',
      msbType: '',
      yield: null,
      maturityDate: '',
      issueDate: '',
      price: null,
    });

    const editForm = ref({
      msbType: '',
      yield: null,
      price: null,
    });

    // File upload handlers
    const handleFileSelect = event => {
      const file = event.target.files[0];
      if (!file) {
        clearFile();
        return;
      }

      const fileNamePattern = /^통안_\d{8}\.xlsx$/;
      const fileName = file.name.normalize('NFC');

      if (fileNamePattern.test(fileName)) {
        selectedFileName.value = fileName;
        selectedFile.value = file;
      } else {
        alert('파일명은 "통안_YYYYMMDD.xlsx" 형식이어야 합니다.');
        event.target.value = '';
        clearFile();
      }
    };

    const clearFile = () => {
      selectedFileName.value = '';
      selectedFile.value = null;
    };

    const uploadFile = async () => {
      if (!selectedFile.value) {
        alert('파일을 선택해주세요.');
        return;
      }

      isUploading.value = true;
      try {
        const formData = new FormData();
        formData.append('file', selectedFile.value);
        formData.append('fileName', selectedFile.value.name.normalize('NFC'));
        formData.append('date', props.selectedDate);
        formData.append('bondType', 'MSB');

        await bondService.uploadBondFile(props.selectedDate, selectedFile.value.name.normalize('NFC'), 'MSB', selectedFile.value);
        alert('파일이 성공적으로 업로드되었습니다.');
        clearFile();
        // 업로드 후 데이터 새로고침
        await loadMsbBonds(props.selectedDate);
      } catch (error) {
        console.error('파일 업로드 중 오류 발생:', error);
        alert('파일 업로드에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isUploading.value = false;
      }
    };

    const analyzePriceData = async () => {
      isAnalyzing.value = true;
      try {
        await bondService.upsertBondOfDay(props.selectedDate, 'MSB');
        // 분석 후 데이터 새로고침
        await loadMsbBonds(props.selectedDate);
        alert('가격정보 분석이 완료되었습니다.');
      } catch (error) {
        console.error('데이터 분석 중 오류 발생:', error);
        alert('데이터 분석에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isAnalyzing.value = false;
      }
    };

    const refreshInfomaxPrices = async () => {
      if (!confirm('INFOMAX에서 민평 정보를 일괄 업데이트합니다.\n이 작업은 시간이 오래 걸릴 수 있습니다.(최대 1~2분 소요)')) {
        return;
      }

      isRefreshingPrices.value = true;
      try {
        const result = await bondService.refreshPrices(props.selectedDate, 'MSB');
        const count = result.length;
        alert(`민평 일괄 업데이트가 완료되었습니다. (${count}개)`);
        // 업데이트 후 데이터 새로고침
        await loadMsbBonds(props.selectedDate);
      } catch (error) {
        console.error('INFOMAX 민평 업데이트 중 오류 발생:', error);

        if (error.errorCode === 'ACCESS_LIMIT_EXCEEDED') {
          alert('INFOMAX API 호출 한도를 초과했습니다.\n잠시 후 다시 시도해주세요.');
        } else {
          alert('INFOMAX 민평 일괄 업데이트에 실패했습니다. 다시 시도해주세요.');
        }
      } finally {
        isRefreshingPrices.value = false;
      }
    };

    // Transaction file upload handlers
    const handleTransactionFileSelect = event => {
      const file = event.target.files[0];
      if (!file) {
        clearTransactionFile();
        return;
      }

      const fileNamePattern = /^통안거래내역_\d{8}\.xlsx$/;
      const fileName = file.name.normalize('NFC');

      if (fileNamePattern.test(fileName)) {
        selectedTransactionFileName.value = fileName;
        selectedTransactionFile.value = file;
      } else {
        alert('파일명은 "통안거래내역_YYYYMMDD.xlsx" 형식이어야 합니다.');
        event.target.value = '';
        clearTransactionFile();
      }
    };

    const clearTransactionFile = () => {
      selectedTransactionFileName.value = '';
      selectedTransactionFile.value = null;
    };

    const uploadTransactionFile = async () => {
      if (!selectedTransactionFile.value) {
        alert('파일을 선택해주세요.');
        return;
      }

      isUploadingTransaction.value = true;
      try {
        await transactionService.uploadTransactionFile(
          props.selectedDate,
          selectedTransactionFile.value.name.normalize('NFC'),
          'MSB',
          selectedTransactionFile.value
        );
        alert('거래내역 파일이 성공적으로 업로드되었습니다.');
        clearTransactionFile();
      } catch (error) {
        console.error('거래내역 파일 업로드 중 오류 발생:', error);
        alert('거래내역 파일 업로드에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isUploadingTransaction.value = false;
      }
    };

    const analyzeTransactionData = async () => {
      isAnalyzingTransaction.value = true;
      try {
        const result = await transactionService.refreshTransactionFromExcel(props.selectedDate, 'MSB');
        alert(`거래내역 분석이 완료되었습니다. (처리된 건수: ${result || 0}건)`);
      } catch (error) {
        console.error('거래내역 분석 중 오류 발생:', error);
        alert('거래내역 분석에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isAnalyzingTransaction.value = false;
      }
    };

    const refreshInfomaxTransactions = async () => {
      if (!confirm('INFOMAX에서 거래내역 정보를 일괄 업데이트합니다.\n이 작업은 시간이 오래 걸릴 수 있습니다.(최대 1~2분 소요)')) {
        return;
      }

      isRefreshingTransactionPrices.value = true;
      try {
        const result = await transactionService.refreshTransactions(props.selectedDate, 'MSB');
        const count = result || 0;
        alert(`거래내역 일괄 업데이트가 완료되었습니다. (${count}건)`);
        // 업데이트 후 데이터 새로고침
        await loadMsbBonds(props.selectedDate);
      } catch (error) {
        console.error('INFOMAX 거래내역 업데이트 중 오류 발생:', error);

        if (error.errorCode === 'ACCESS_LIMIT_EXCEEDED') {
          alert('INFOMAX API 호출 한도를 초과했습니다.\n잠시 후 다시 시도해주세요.');
        } else {
          alert('INFOMAX 거래내역 일괄 업데이트에 실패했습니다. 다시 시도해주세요.');
        }
      } finally {
        isRefreshingTransactionPrices.value = false;
      }
    };

    // Loading message helper
    const getLoadingMessage = () => {
      if (isUploading.value) return '기본정보 파일을 업로드하는 중...';
      if (isAnalyzing.value) return '가격정보 분석 중...';
      if (isRefreshingPrices.value) return 'INFOMAX 민평 일괄 업데이트 중...';
      if (isUploadingTransaction.value) return '거래내역 파일을 업로드하는 중...';
      if (isAnalyzingTransaction.value) return '거래내역 분석 중...';
      if (isRefreshingTransactionPrices.value) return 'INFOMAX 거래내역 업데이트 중...';
      return '처리 중...';
    };

    // Modal handlers
    const openCreateModal = () => {
      createForm.value = {
        bondName: '',
        code: '',
        msbType: '',
        yield: null,
        maturityDate: '',
        issueDate: '',
        price: null,
      };
      showCreateModal.value = true;
    };

    const closeCreateModal = () => {
      showCreateModal.value = false;
    };

    const handleCreate = async () => {
      try {
        await createMsbBond(createForm.value);
        closeCreateModal();
      } catch (error) {
        alert(error.message);
      }
    };

    const openEditModal = bond => {
      selectedBond.value = bond;
      editForm.value = {
        msbType: bond.msbType,
        yield: bond.yield,
        price: bond.price,
      };
      showEditModal.value = true;
    };

    const closeEditModal = () => {
      showEditModal.value = false;
      selectedBond.value = null;
    };

    const handleUpdate = async () => {
      try {
        await updateMsbBond(selectedBond.value, editForm.value, props.selectedDate);
        closeEditModal();
      } catch (error) {
        alert(error.message);
      }
    };

    // Drag and drop handlers
    const handleDrop = async (event, targetType) => {
      await onDrop(event, targetType, async (bond, newType) => {
        await updateMsbType(bond, newType, props.selectedDate);
      });
    };

    onMounted(() => {
      loadMsbBonds(props.selectedDate);
    });

    return {
      // Data
      msbTypes,
      typeOrder,
      isLoading,
      draggedBond,

      // File upload
      selectedFile,
      selectedFileName,
      isUploading,
      isAnalyzing,
      isRefreshingPrices,


      // Transaction file upload
      selectedTransactionFile,
      selectedTransactionFileName,
      isUploadingTransaction,
      isAnalyzingTransaction,
      isRefreshingTransactionPrices,

      // Modal states
      showCreateModal,
      showEditModal,
      selectedBond,
      createForm,
      editForm,

      // Methods
      handleFileSelect,
      clearFile,
      uploadFile,
      analyzePriceData,
      refreshInfomaxPrices,
      handleTransactionFileSelect,
      clearTransactionFile,
      uploadTransactionFile,
      analyzeTransactionData,
      refreshInfomaxTransactions,
      getLoadingMessage,
      openCreateModal,
      closeCreateModal,
      handleCreate,
      openEditModal,
      closeEditModal,
      handleUpdate,
      getMsbTypeName,
      getFilteredBonds,

      // Drag and drop
      onDragStart,
      onDragEnd,
      onDragOver,
      onDragLeave,
      handleDrop,
      isDragOverTarget,
    };
  },
};
</script>

<style scoped>
.btn-secondary {
  @apply inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-secondary:focus {
  @apply outline-none ring-2 ring-offset-2 ring-gray-500;
}
</style>
